import React, { useEffect, useState } from 'react';

import { isEqual } from 'lodash';
import { useQuery } from 'react-apollo';

import { useI18n, useSelector } from 'Hooks';
import CreateModificationGroup from 'Scenes-redesign/PIM/Components/CreateContent/ModificationGroup';
import { getIngredientsList } from 'Scenes-redesign/PIM/utils';

import { getLangText } from '../../../utils/langText';
import AddButton from '../../AddButton';
import SubmitButton from '../../Form/SubmitButton';
import List from '../../List';
import useModal from '../../Modal/useModal';
import NoResultFound from '../../NoResultFound';
import SideModal from '../../SideModal';
import Titlebar from '../../Titlebar';
import { Heading } from '../../Typography/Heading';
import { Paragraph } from '../../Typography/Paragraph';
import SearchInput from '../SearchInput';
import { GetCompanyData } from './__queries__';
import CreateNew, { COMPANY_QUERY } from './CreateNew';
import { AddButtonWrapper, BarWrapper, SideModalWrapper, Text, Wrapper } from './styles';
import { formatList, getCorrectName, grouplist } from './utils';
import ValueList from './valueList';

export interface Props {
  idToUpdate: string | undefined;
  formId: string;
  title?: string;
  description?: string;
  values: any[];
  list: any[];
  handleChange: (values: any) => void;
  selectedValues?: string[];
  handleSelectValues?: (value: string) => void;
  isModalOpen: boolean;
  setAddedNewIngredient?: React.Dispatch<React.SetStateAction<boolean>>;
  handleOrderChange?: (values: any) => void;
}

const FieldSelectList = ({
  idToUpdate,
  formId,
  title,
  description,
  values = [],
  list = [],
  handleChange,
  isModalOpen = false,
  selectedValues,
  handleSelectValues,
  setAddedNewIngredient,
  handleOrderChange,
}: Props) => {
  const companyData = useSelector((state) => state.companyData);
  const [selected, setSelected] = useState<any>([]);
  const [itemsList, setItemsList] = useState<any>([]);
  const [searchValue, setSearchValue] = useState('');
  const { isShowing, toggle, hide } = useModal();
  const { isShowing: isGroupShowing, toggle: toggleGroup, hide: hideGroup } = useModal();
  const [groupId, setGroupId] = useState();
  const { t, defaultLanguage } = useI18n();

  const { data } = useQuery<GetCompanyData>(COMPANY_QUERY, {
    variables: {
      companyId: companyData?.id,
    },
    fetchPolicy: 'cache-only',
  });

  const variationsList = data?.company ? getIngredientsList(data.company.variations) : [];

  useEffect(() => {
    if (isModalOpen) toggle();
  }, [isModalOpen]);

  useEffect(() => {
    setItemsList(grouplist(list, formId));
  }, [list]);

  useEffect(() => {
    if (groupId) toggleGroup();
  }, [groupId]);

  useEffect(() => {
    const filteredItems = list.filter((item) => {
      if (item.categories) {
        return (
          getCorrectName(item).toLowerCase().includes(searchValue.toLowerCase()) ||
          item.categories[0]?.name.toLowerCase().includes(searchValue.toLowerCase())
        );
      } else {
        return getCorrectName(item).toLowerCase().includes(searchValue.toLowerCase());
      }
    });

    setItemsList(grouplist(filteredItems, formId));
  }, [searchValue]);

  const valuesList: any = formatList(t, formId, values, defaultLanguage);

  useEffect(() => {
    const equal = isEqual(values, selected);
    if (!equal && idToUpdate) {
      setSelected(values);
    }
  }, [values, idToUpdate]);

  const handleSubmit = () => {
    handleChange(selected);
    hide();
  };

  const handleAdd = (item) => {
    setSelected((prev) => [...prev, item]);
  };

  const addNewToList = (node) => {
    const formattedNode = node;

    formattedNode.name = getLangText(node.nameLang, defaultLanguage) ?? node.name ?? node.title;
    handleAdd(formattedNode);
    setItemsList(grouplist([...itemsList[0].items, formattedNode], formId));
    if (setAddedNewIngredient) {
      setAddedNewIngredient(true);
    }
  };

  const handleAddAll = (items) => {
    const itemsToAdd = items.filter((item) => item.addHandler);

    itemsToAdd.map((item) => {
      item.title = item.name;
      handleAdd(item);
    });
  };

  const handleRemoveSelected = (listItem) => {
    const newList = selected.filter(
      (prevItem) => (prevItem.group?.id ?? prevItem.id) !== listItem.id,
    );
    setSelected(newList);
  };

  const removeValue = (id) => {
    const newValues = values.filter((val) => val.id !== id);
    setSelected((prev) => prev.filter((prevItem) => prevItem.id !== id));
    handleChange(newValues);
  };

  const handleEdit = (listItem) => {
    setGroupId(listItem.id);
  };

  const closeGroupModal = () => {
    setGroupId(undefined);
    hideGroup();
  };

  const addTitle = {
    items: t('General.AddArticles'),
    variations: t('General.AddIngredients'),
    upsales: t('General.AddUpsales'),
    itemsUsedOn: t('General.AddArticles'),
    defaultVariations: t('General.AddIngredients'),
    alternateVariations: t('General.AddIngredientGroups'),
    extraIngredients: t('General.AddExtraIngredients'),
    modifications: 'Add modification groups',
  };

  const hasAddButton = Boolean(
    formId == 'upsales' ||
      formId == 'itemsUsedOn' ||
      formId == 'defaultVariations' ||
      formId == 'alternateVariations' ||
      formId == 'items' ||
      formId == 'extraIngredients' ||
      formId == 'modifications',
  );

  const typeText = {
    variations: 'ingredient',
    upsales: 'upsell',
    defaultVariations: 'ingredient',
    extraIngredients: 'ingredient',
    modifications: 'ingredient',
  };

  return (
    <>
      {title && formId !== 'modifications' && (
        <BarWrapper>
          <Text>
            <Heading level={5} color="var(--colorWhite)" bottom={2}>
              {title}
            </Heading>
            {description && (
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {description}
              </Paragraph>
            )}
          </Text>
        </BarWrapper>
      )}
      {valuesList?.map((valueList, index) => {
        return (
          <Wrapper key={index}>
            {valueList.barTitle && (
              <BarWrapper>
                <Text>
                  <Heading level={5} color="var(--colorWhite)" bottom={0}>
                    {valueList.barTitle}
                  </Heading>
                </Text>
              </BarWrapper>
            )}
            <ValueList
              valueList={valueList}
              formId={formId}
              removeValue={(id) => removeValue(id)}
              selectedValues={selectedValues ?? []}
              selectValues={handleSelectValues ? (values) => handleSelectValues(values) : undefined}
              handleOrderChange={handleOrderChange}
            />

            {hasAddButton && (
              <AddButtonWrapper>
                <AddButton text={addTitle[formId] ?? t('General.Add')} handleClick={toggle} />
              </AddButtonWrapper>
            )}
          </Wrapper>
        );
      })}
      <SideModal isOpen={isShowing} closeModal={hide}>
        <Titlebar title={addTitle[formId] ?? t('General.Add')} height="60px" />
        <SideModalWrapper>
          <SearchInput
            placeholder={t('General.Search')}
            value={searchValue}
            onChange={setSearchValue}
            sticky
          />
          {itemsList[0]?.items?.length < 1 && list.length > 0 && (
            <NoResultFound
              title={`${t('General.CouldntFindResults')} ${searchValue}`}
              text={
                typeText[formId]
                  ? `${t('General.CheckSpellingOrCreate')} ${typeText[formId]}`
                  : t('General.CheckSpelling')
              }
            />
          )}
          {itemsList.map((item, index) => {
            const sortedItems = item?.items.sort((a, b) => {
              if (a.displayOrder || b.displayOrder) {
                return a.displayOrder < b.displayOrder ? 1 : -1;
              }
              if (a.order || b.order) {
                return a.order < b.order ? 1 : -1;
              }
              return getCorrectName(a) >= getCorrectName(b) ? 1 : -1;
            });

            return (
              <List
                key={index}
                title={item.category ?? ''}
                handleAddAll={(items) => handleAddAll(items)}
                isPrice
                listItems={sortedItems.map((listItem) => {
                  const alreadyAdded = selected.find((selected) => {
                    return (
                      selected.id == listItem.id ||
                      selected.item?.id == listItem.id ||
                      selected.group?.id == listItem.id
                    );
                  });

                  return {
                    id: listItem.id ?? '',
                    name: getCorrectName(listItem),
                    categories: listItem.categories,
                    value: listItem.price,
                    defaultPrice: listItem.defaultPrice,
                    addHandler: !alreadyAdded ? () => handleAdd(listItem) : null,
                    removeHandler: alreadyAdded ? () => handleRemoveSelected(listItem) : null,
                    editHandler: null,
                  };
                })}
              />
            );
          })}
          <CreateNew formId={formId} addNew={(node) => addNewToList(node)} />
        </SideModalWrapper>
        <SubmitButton buttonText={t('General.Done')} handleSubmit={handleSubmit} floating />
      </SideModal>
      <CreateModificationGroup
        idToUpdate={groupId}
        isShowing={isGroupShowing}
        variationsList={variationsList}
        closeModal={closeGroupModal}
        handleFormSuccess={() => setGroupId(undefined)}
      />
    </>
  );
};

export default FieldSelectList;
